<template>
  <v-row>
    <v-col cols="12" class="pb-0">
      <v-card tile flat class="pa-2" id="documents-note-text">
        <v-card-text id="please-note-verbiage">
          {{ $t('L_view_statements_for') }}
          <v-col cols="4">
            <OnexSelect
              class="pb-0"
              v-model="viewFilter"
              :items="viewFilterItems"
              :label="$t('')"
              :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
            />
          </v-col>
          {{ $t('L_please_note_that_some_documents') }}
        </v-card-text>
      </v-card>
    </v-col>
    <v-col cols="12" class="pt-0">
      <v-card tile align-stretch flat class="pl-2">
        <v-container class="p-0">
          <div v-if="isCanadian" class="mutual_fund_account_css text-wrap mt-4 mr-4 mb-4">
            {{
              $t(
                'L_only_mutual_fund_account_statements_are_available_to_view_on_myprimerica_for_common_sense'
              )
            }}<a href="javascript:void(0)" @click="agfLink()">www.agf.com</a>
          </div>
        </v-container>
        <v-card flat v-if="docs.length > 0">
          <v-data-table-virtual
            height="400"
            fixed-header
            :headers="headers"
            :items="docs"
            item-value="name"
            hide-default-footer
          >
            <template v-slot:item="props">
              <tr class="text-no-wrap">
                <td>{{ (props.item as any).date }}</td>
                <td>{{ $t(`L_${(props.item as any).type}`) }}</td>
                <td class="cursor-pointer">
                  <a @click="getDocumentPDF(props.item)">{{
                    (props.item as any).statementPeriod
                  }}</a>
                </td>
                <td>
                  <v-icon @click="downloadPDF(props.item)" color="#052f60" :icon="mdiDownload" />
                </td>
              </tr>
            </template>
          </v-data-table-virtual>
        </v-card>
        <v-card flat v-else>
          <v-card-text>
            <div class="py-5">
              <v-img
                id="documents-folder-img"
                height="180"
                width="180"
                src="@/assets/documentsFolder.png"
                alt="documents-folder"
                class="d-flex mx-auto"
              />
            </div>
            <div id="you-have-no-available" class="font-weight-bold text-center">
              {{ $t('L_no_documents_currently_available') }}
            </div>
          </v-card-text>
        </v-card>
      </v-card>
    </v-col>
    <v-col cols="12" class="container" />
  </v-row>
</template>

<script lang="ts">
import { openInNewWindow } from '@/mixins/openNewWindow';
import { formatDate as formatDateUtil } from '@/shared/util/formatters';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mapActions, mapState, mapStores } from 'pinia';
import { useGlobalStore } from '@/stores/global/global.store';
import { type ComponentOptions, defineComponent } from 'vue';
import { COUNTRIES } from '@/shared/models/constants.models';
import { formatDate } from 'date-fns';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { mdiDownload } from '@mdi/js';
import { OnexSelect } from '@primerica/onex-ui-lib';
import type { IGlobalStoreDocumentsInvestmentStatementItem } from '@/stores/global/globalStore.model';

const componenOptions: ComponentOptions = {
  name: 'InvestmentStatements',
  components: {
    OnexSelect,
  },
  mixins: [openInNewWindow],
  data() {
    return {
      viewFilter: new Date().getFullYear(),
      mdiDownload,
    };
  },
  computed: {
    ...mapStores(useGlobalStore),
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      investmentStatements: 'investmentStatements',
      base64String: 'base64String',
    }),
    ...mapState(useCustomerStore, ['binaryInvestmentStatement', 'customerProfile']),
    isCanadian() {
      return this.customerProfile?.country === COUNTRIES.CANADA;
    },
    headers() {
      return [
        {
          title: this.$t('L_date'),
          align: 'left',
          value: 'date',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_type'),
          align: 'left',
          value: 'type',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_statement_period'),
          align: 'left',
          value: 'statementPeriod',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
        {
          title: this.$t('L_download'),
          value: 'download',
          sortable: false,
          class: 'primarylight font-weight-black',
        },
      ];
    },
    filteredInvestmentStatements() {
      const cutoffDate = new Date(this.viewFilter, 0, 1);
      const filteredDocumentsList = this.investmentStatements.filter(
        (doc: { dateReceived: string }) => {
          const isWithinCutoff =
            new Date(doc.dateReceived).getFullYear() === cutoffDate.getFullYear();
          return isWithinCutoff;
        }
      );
      return this.viewFilter === 0 ? this.investmentStatements : filteredDocumentsList;
    },
    docs() {
      const d = this.filteredInvestmentStatements;
      const r = d?.map((i: IGlobalStoreDocumentsInvestmentStatementItem) => {
        const startMonth = i.name?.period.start.month
          ? this.$t(`L_${i.name.period.start.month.toLowerCase()}`)
          : '';
        const endMonth = i.name?.period.end.month
          ? this.$t(`L_${i.name.period.end.month.toLowerCase()}`)
          : '';
        const statementPeriod = `${startMonth} ${i.name?.period.start.day} - ${endMonth} ${i.name?.period.end.day}, ${i.name?.period.year}`;
        return {
          date: i.dateReceived
            ? formatDateUtil(formatDate(new Date(i.dateReceived), 'yyyy-MM-dd'), this.dateFormat)
            : '',
          statementPeriod,
          type: i.type,
          doc: i,
        };
      });
      return r || [];
    },
    viewFilterItems() {
      const currentYear = new Date().getFullYear();
      const items = [{ title: currentYear.toString(), value: currentYear }];
      for (let yr = 1; yr < 7; yr++) {
        items.push({ title: currentYear - yr + '', value: currentYear - yr });
      }
      items.push({ title: this.$t('L_all'), value: 0 });
      return items;
    },
  },
  methods: {
    ...mapActions(useCustomerStore, ['openStatement']),
    async agfLink() {
      await this.openInNewWindow({ url: 'http://www.agf.com/', target: '_blank' });
    },
    async downloadPDF(item?: {
      date: string;
      statementPeriod: string;
      type: string;
      doc?: IGlobalStoreDocumentsInvestmentStatementItem;
    }) {
      try {
        const t = `view_doc_${item?.statementPeriod[0].toLowerCase()}${item?.statementPeriod
          .slice(1)
          .replace(/ /g, '')}`;
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      // * see https://dev.to/nombrekeff/download-file-from-blob-21ho
      // Create a link pointing to the ObjectURL containing the blob
      const blobURL = await this.getInvestmentDocument(item?.doc?.metadata);
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = blobURL as string;
      tempLink.setAttribute('download', item?.statementPeriod ?? '');
      // Safari thinks _blank anchor are pop ups. We only want to set _blank
      // target if the browser does not support the HTML5 download attribute.
      // This allows you to download files in desktop safari if pop up blocking
      // is enabled.
      if (typeof tempLink.download === 'undefined') {
        tempLink.setAttribute('target', '_blank');
      }
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    },
    async getDocumentPDF(item: {
      date: string;
      statementPeriod: string;
      type: string;
      doc: IGlobalStoreDocumentsInvestmentStatementItem;
    }) {
      try {
        const s = item.statementPeriod;
        const t = `view_doc_${s[0].toLowerCase()}${s.slice(1).replaceAll(' ', '')}`;
        const i = this.$dynatrace.enterAction(t);
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      const url = await this.getInvestmentDocument(item.doc.metadata);
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, inappbrowser: !isWeb });
    },
    getInvestmentDocument(requestObject: {
      date: string;
      name: string;
      type: string;
      doc: IGlobalStoreDocumentsInvestmentStatementItem;
    }) {
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.openStatement(requestObject).then(() =>
          URL.createObjectURL(
            new Blob([this.binaryInvestmentStatement as BlobPart], { type: 'application/pdf' })
          )
        );
      } else {
        return this.openStatement(requestObject).then(() => this.binaryInvestmentStatement);
      }
    },
  },
  beforeRouteEnter(_, __, next) {
    // called before the route that renders this component is confirmed.
    // does NOT have access to `this` component instance,
    // because it has not been created yet when this guard is called!
    next((vm) => {
      // access to component public instance via `vm`
      try {
        const i = vm.$dynatrace.enterAction('view_documentsPage');
        vm.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    });
  },
  beforeRouteUpdate() {
    // called when the route that renders this component has changed,
    // but this component is reused in the new route.
    try {
      const i = this.$dynatrace.enterAction('view_documentsPage');
      this.$dynatrace.leaveAction(i);
    } catch (_error) {
      /* empty */
    }
  },
};
export default defineComponent(componenOptions);
</script>

<style lang="scss" scoped>
table.v-table tbody td {
  font-size: 14px !important;
  padding: 24px !important;
}

.v-datatable.v-table thead th:first-child {
  padding-left: 24px !important;
}

#documents-note-text {
  border-bottom: solid 1px #ccc;
}

#please-note-verbiage {
  padding: 16px;
}

.mutual_fund_account_css {
  font-size: 14px;
  font-weight: 500;
}
</style>
